import { useState, type ReactNode } from 'react';
import { Filter as FilterIcon, RotateCcw } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FilterDateRange } from '@/components/tables';
import { codeToFlagEmoji, type CountryItem } from '@/resources/countries/types';
import { getFlagEmoji, type Language } from '@/resources/languages/types';

type StatusFilter = 'all' | 'active' | 'blocked' | 'deleted';
type YesNoFilter = 'all' | 'yes' | 'no';

interface TagOption {
  id: number;
  name: string;
  color: string;
}

interface AcquisitionOption {
  value: string;
  label: string;
}

export interface UsersFiltersValue {
  statusFilter: StatusFilter;
  tagFilter: string;
  languageFilter: string;
  countryFilter: string;
  currencyFilter: string;
  acquisitionFilter: string;
  hasEmailFilter: YesNoFilter;
  emailVerifiedFilter: YesNoFilter;
  createdAtFrom?: Date;
  createdAtTo?: Date;
  lastPaymentFrom?: Date;
  lastPaymentTo?: Date;
}

interface UsersFiltersSheetProps {
  value: UsersFiltersValue;
  onChange: (next: UsersFiltersValue) => void;
  availableTags: TagOption[];
  availableLanguages: Language[];
  availableCountries: CountryItem[];
  availableCurrencies: string[];
  acquisitionOptions: AcquisitionOption[];
}

const EMPTY_VALUE: UsersFiltersValue = {
  statusFilter: 'all',
  tagFilter: '',
  languageFilter: '',
  countryFilter: '',
  currencyFilter: '',
  acquisitionFilter: '',
  hasEmailFilter: 'all',
  emailVerifiedFilter: 'all',
  createdAtFrom: undefined,
  createdAtTo: undefined,
  lastPaymentFrom: undefined,
  lastPaymentTo: undefined,
};

/**
 * Считаем количество активных (не-«все») фильтров для бейджа на кнопке.
 */
export function countActiveFilters(v: UsersFiltersValue): number {
  let n = 0;
  if (v.statusFilter !== 'all') n++;
  if (v.tagFilter) n++;
  if (v.languageFilter) n++;
  if (v.countryFilter) n++;
  if (v.currencyFilter) n++;
  if (v.acquisitionFilter) n++;
  if (v.hasEmailFilter !== 'all') n++;
  if (v.emailVerifiedFilter !== 'all') n++;
  if (v.createdAtFrom || v.createdAtTo) n++;
  if (v.lastPaymentFrom || v.lastPaymentTo) n++;
  return n;
}

/**
 * Side-panel со всеми фильтрами таблицы пользователей.
 *
 * Локально (внутри Sheet) держим draft-копию, чтобы пользователь видел изменения
 * до нажатия «Применить». В onChange отдаём только при «Применить» — это
 * избавляет от лишних запросов к backend при каждом клике в селекте.
 */
export function UsersFiltersSheet({
  value,
  onChange,
  availableTags,
  availableLanguages,
  availableCountries,
  availableCurrencies,
  acquisitionOptions,
}: UsersFiltersSheetProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<UsersFiltersValue>(value);
  const activeCount = countActiveFilters(value);

  const handleOpenChange = (next: boolean) => {
    if (next) {
      // При открытии — синхронизируемся с внешним value.
      setDraft(value);
    }
    setOpen(next);
  };

  const update = <K extends keyof UsersFiltersValue>(
    key: K,
    val: UsersFiltersValue[K],
  ) => {
    setDraft((prev) => ({ ...prev, [key]: val }));
  };

  const handleApply = () => {
    onChange(draft);
    setOpen(false);
  };

  const handleReset = () => {
    setDraft(EMPTY_VALUE);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <FilterIcon className="h-4 w-4" />
          Фильтры
          {activeCount > 0 && (
            <Badge
              variant="secondary"
              className="ml-1 h-5 min-w-5 rounded-full px-1.5 tabular-nums"
            >
              {activeCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="sm:max-w-md w-full p-0 flex flex-col gap-0"
        title="Фильтры пользователей"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <SheetTitle>Фильтры</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          <FilterGroup title="Аккаунт">
            <FieldRow label="Статус аккаунта">
              <Select
                value={draft.statusFilter}
                onValueChange={(val) => update('statusFilter', val as StatusFilter)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все</SelectItem>
                  <SelectItem value="active">Активные</SelectItem>
                  <SelectItem value="blocked">Заблокированные</SelectItem>
                  <SelectItem value="deleted">Удалённые</SelectItem>
                </SelectContent>
              </Select>
            </FieldRow>
            <FieldRow label="Email">
              <Select
                value={draft.hasEmailFilter}
                onValueChange={(val) => update('hasEmailFilter', val as YesNoFilter)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Любой</SelectItem>
                  <SelectItem value="yes">С email</SelectItem>
                  <SelectItem value="no">Без email</SelectItem>
                </SelectContent>
              </Select>
            </FieldRow>
            <FieldRow label="Подтверждение email">
              <Select
                value={draft.emailVerifiedFilter}
                onValueChange={(val) =>
                  update('emailVerifiedFilter', val as YesNoFilter)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Любой</SelectItem>
                  <SelectItem value="yes">Подтверждён</SelectItem>
                  <SelectItem value="no">Не подтверждён</SelectItem>
                </SelectContent>
              </Select>
            </FieldRow>
          </FilterGroup>

          <FilterGroup title="Категоризация">
            <FieldRow label="Тег">
              <Select
                value={draft.tagFilter || 'all'}
                onValueChange={(val) => update('tagFilter', val === 'all' ? '' : val)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Все теги" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все теги</SelectItem>
                  {availableTags.map((tag) => (
                    <SelectItem key={tag.id} value={String(tag.id)}>
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{ backgroundColor: tag.color }}
                        />
                        {tag.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldRow>
            <FieldRow label="Канал привлечения">
              <Select
                value={draft.acquisitionFilter || 'all'}
                onValueChange={(val) =>
                  update('acquisitionFilter', val === 'all' ? '' : val)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Все каналы" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все каналы</SelectItem>
                  {acquisitionOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldRow>
          </FilterGroup>

          <FilterGroup title="Локаль">
            <FieldRow label="Страна">
              <Select
                value={draft.countryFilter || 'all'}
                onValueChange={(val) =>
                  update('countryFilter', val === 'all' ? '' : val)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Все страны" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все страны</SelectItem>
                  {availableCountries.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      <div className="flex items-center gap-2">
                        <span>{codeToFlagEmoji(c.code)}</span>
                        <span>{c.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldRow>
            <FieldRow label="Язык интерфейса">
              <Select
                value={draft.languageFilter || 'all'}
                onValueChange={(val) =>
                  update('languageFilter', val === 'all' ? '' : val)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Все языки" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все языки</SelectItem>
                  {availableLanguages.map((language) => (
                    <SelectItem key={language.code} value={language.code}>
                      <div className="flex items-center gap-2">
                        <span>{language.flagEmoji || getFlagEmoji(language.code)}</span>
                        {language.nativeName || language.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldRow>
            <FieldRow label="Валюта">
              <Select
                value={draft.currencyFilter || 'all'}
                onValueChange={(val) =>
                  update('currencyFilter', val === 'all' ? '' : val)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Все валюты" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все валюты</SelectItem>
                  {availableCurrencies.map((cur) => (
                    <SelectItem key={cur} value={cur}>
                      {cur}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldRow>
          </FilterGroup>

          <FilterGroup title="Даты">
            <FilterDateRange
              label="Регистрация"
              from={draft.createdAtFrom}
              to={draft.createdAtTo}
              onFromChange={(d) => update('createdAtFrom', d)}
              onToChange={(d) => update('createdAtTo', d)}
            />
            <FilterDateRange
              label="Последняя оплата"
              from={draft.lastPaymentFrom}
              to={draft.lastPaymentTo}
              onFromChange={(d) => update('lastPaymentFrom', d)}
              onToChange={(d) => update('lastPaymentTo', d)}
            />
          </FilterGroup>
        </div>

        <SheetFooter className="px-6 py-4 border-t flex-row gap-2 sm:flex-row sm:justify-between">
          <Button
            variant="ghost"
            onClick={handleReset}
            className="gap-2"
            disabled={countActiveFilters(draft) === 0}
          >
            <RotateCcw className="h-4 w-4" />
            Сбросить
          </Button>
          <div className="flex gap-2">
            <SheetClose asChild>
              <Button variant="outline">Отмена</Button>
            </SheetClose>
            <Button onClick={handleApply}>Применить</Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function FilterGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">{label}</Label>
      {children}
    </div>
  );
}
